import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Msdyn365 from '@msdyn365-commerce/core';
import ConfirmationService, { ConfirmEvent } from './ConfirmationService';

interface IConfirmModalResources {
    yesButtonLabel: string;
    noButtonLabel: string;
}

const DefaultResources: { [locale: string]: IConfirmModalResources } = {
    'en-US': {
        yesButtonLabel: 'OK',
        noButtonLabel: 'Cancel'
    },
    'nb-NO': {
        yesButtonLabel: 'OK',
        noButtonLabel: 'Avbryt'
    }
};

function getResources(locale: string) {
    if (DefaultResources[locale]) {
        return DefaultResources[locale];
    } else {
        return DefaultResources['en-US'];
    }
}

const ConfirmModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [resolveCallback, setResolveCallback] = useState<((confirmed: boolean) => void) | null>(null);

    useEffect(() => {
        const handleConfirm = ({ title, message, resolve }: ConfirmEvent) => {
            setTitle(title);
            setMessage(message);
            setIsOpen(true);
            setResolveCallback(() => resolve);
        };

        ConfirmationService.onConfirm(handleConfirm);
        return () => ConfirmationService.offConfirm(handleConfirm);
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.setProperty('--confirm-modal-top', `-${document.documentElement.scrollTop}px`);
            document.body.classList.add('confirm-modal-open');
        } else {
            document.body.classList.remove('confirm-modal-open');
            window.scrollTo(0, Math.abs(parseInt(document.body.style.getPropertyValue('--confirm-modal-top') || '0', 10)));
        }
    }, [isOpen]);

    const handleConfirm = () => {
        if (resolveCallback) {
            resolveCallback(true);
        }
        setIsOpen(false);
    };

    const handleCancel = () => {
        if (resolveCallback) {
            resolveCallback(false);
        }
        setIsOpen(false);
    };

    if (!isOpen) {
        return null;
    }

    const baseClassName = 'confirm-modal';

    let locale = 'en-US';

    if (Msdyn365.isBrowser) {
        if ((window as any).___initialData___?.requestContext?.locale) {
            locale = (window as any).___initialData___.requestContext.locale;
        }
    }

    const resources = getResources(locale);

    return ReactDOM.createPortal(
        <div className={baseClassName}>
            <div className={`${baseClassName}__content`}>
                <h2 className={`${baseClassName}__heading`}>
                    {title}
                </h2>
                <p className={`${baseClassName}__text`}>
                    {message}
                </p>
                <div className={`${baseClassName}__button-container`}>
                    <button className={`${baseClassName}__button__no`} onClick={handleCancel}>
                        {resources.noButtonLabel}
                    </button>
                    <button className={`${baseClassName}__button__yes`} onClick={handleConfirm}>
                        {resources.yesButtonLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmModal;
